import React from 'react';
import { Link } from 'react-router-dom';
import { Shield, Mail, Phone, Calendar } from 'lucide-react';
import SEO from '../components/SEO';

const Privacy = () => {
  return (
    <div className="min-h-screen bg-gray-50">
      <SEO title="Privacy Policy | Audio & Co®" description="How Audio & Co® collects, uses, and protects your personal data. GDPR compliant. Covers SEO audit tool, lead capture, and service delivery." />
      {/* Header */}
      <section className="bg-black text-white py-12">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-red-600 rounded-full mb-6 border-4 border-yellow-400">
              <Shield className="w-8 h-8 text-yellow-400" />
            </div>
            <h1 className="text-3xl md:text-4xl font-black mb-4">
              <span className="text-red-600">Privacy</span> <span className="text-yellow-400">Policy</span>
            </h1>
            <p className="text-xl text-yellow-400 font-bold">
              Your privacy matters to us. Here's how we protect and handle your information.
            </p>
            <p className="text-sm text-white mt-4">
              Last updated: April 2026 | GDPR Compliant
            </p>
          </div>
        </div>
      </section>

      {/* Content */}
      <section className="py-12">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-white rounded-xl shadow-lg p-8 md:p-12">
            
            {/* Quick Summary */}
            <div className="bg-red-50 border-l-4 border-red-600 p-6 mb-8">
              <h2 className="text-lg font-black text-red-600 mb-3">Quick Summary</h2>
              <ul className="text-red-800 space-y-2 text-sm font-semibold">
                <li>✓ We only collect information necessary to provide our services</li>
                <li>✓ We never sell your personal data to third parties</li>
                <li>✓ You have full control over your data and can request deletion anytime</li>
                <li>✓ We use industry-standard security measures to protect your information</li>
                <li>✓ We're fully GDPR compliant for all EU residents</li>
              </ul>
            </div>

            <div className="prose prose-lg max-w-none">
              <h2 className="text-red-600 font-black">1. Information We Collect</h2>
              
              <h3 className="text-yellow-600 font-bold">Information You Provide</h3>
              <p className="text-gray-900">
                When you use our services, we collect information you provide directly, including:
              </p>
              <ul>
                <li><strong>Contact Information:</strong> Name, email address, phone number, company name</li>
                <li><strong>Project Information:</strong> Details about your media projects, goals, and requirements</li>
                <li><strong>Communication Records:</strong> Messages, calls, and correspondence related to our services</li>
                <li><strong>Payment Information:</strong> Billing details processed securely through our payment providers</li>
                <li><strong>Content:</strong> Any content you provide for podcasts, videos, books, or other media projects</li>
                <li><strong>SEO Audit Data:</strong> When you use our free SEO audit tool, we collect your name, email address, and the website URL you submit for analysis. We also collect your consent preferences (privacy/terms acceptance and optional marketing consent)</li>
              </ul>

              <h3 className="text-yellow-600 font-bold">Information Collected via Our SEO Audit Tool</h3>
              <p className="text-gray-900">
                Our free SEO audit tool crawls the publicly available pages of the website URL you provide in order to generate your audit report. This involves:
              </p>
              <ul>
                <li><strong>Website Crawling:</strong> We access and analyse publicly available pages on the website you submit (up to 8 pages). We only read information that is already publicly visible to any visitor or search engine</li>
                <li><strong>Technical Analysis:</strong> We check technical SEO factors including meta tags, schema markup, page speed, security certificates, accessibility features, and AI/voice search readiness</li>
                <li><strong>Data Storage:</strong> Your audit results (including your name, email, website URL, and the audit findings) are stored securely to deliver your report and for our internal records</li>
                <li><strong>No Third-Party Sharing:</strong> Your audit data is never shared with, sold to, or made available to any third party</li>
                <li><strong>Marketing Communications:</strong> If you opt in to marketing, we may send you SEO tips, follow-up recommendations based on your audit, and information about our services. You can unsubscribe at any time with one click</li>
              </ul>

              <h3 className="text-yellow-600 font-bold">Information We Collect Automatically</h3>
              <p className="text-gray-900">
                When you visit our website or use our services, we automatically collect:
              </p>
              <ul>
                <li><strong>Website Analytics:</strong> Pages visited, time spent, referring websites</li>
                <li><strong>Device Information:</strong> Browser type, operating system, IP address</li>
                <li><strong>Cookies:</strong> Small files that help improve your experience (see Cookie Policy below)</li>
              </ul>

              <h2 className="text-red-600 font-black">2. How We Use Your Information</h2>
              
              <p className="text-gray-900">We use your information to:</p>
              <ul>
                <li><strong>Provide Services:</strong> Deliver the media and SEO services you've requested, including generating and delivering SEO audit reports</li>
                <li><strong>Communication:</strong> Respond to inquiries, provide project updates, and deliver audit results to your email</li>
                <li><strong>Improvement:</strong> Enhance our services, improve our audit tool accuracy, and develop new offerings</li>
                <li><strong>Marketing:</strong> Send relevant updates, SEO tips, and offers (only with your explicit consent — you can opt out at any time)</li>
                <li><strong>Legal Compliance:</strong> Meet regulatory requirements and protect our rights</li>
              </ul>

              <h2 className="text-red-600 font-black">3. Legal Basis for Processing (GDPR)</h2>
              
              <p className="text-gray-900">Under GDPR, we process your data based on:</p>
              <ul>
                <li><strong>Contract:</strong> To fulfill our service agreements with you</li>
                <li><strong>Consent:</strong> For marketing communications and optional features</li>
                <li><strong>Legitimate Interest:</strong> To improve our services and prevent fraud</li>
                <li><strong>Legal Obligation:</strong> To comply with applicable laws and regulations</li>
              </ul>

              <h2 className="text-red-600 font-black">4. Data Sharing and Disclosure</h2>
              
              <p className="text-gray-900">We may share your information with:</p>
              <ul>
                <li><strong>Service Providers:</strong> Trusted partners who help deliver our services (hosting, payment processing)</li>
                <li><strong>Professional Advisors:</strong> Legal, accounting, and consulting services</li>
                <li><strong>Legal Requirements:</strong> When required by law or to protect our rights</li>
                <li><strong>Business Transfers:</strong> In case of merger, acquisition, or asset sale</li>
              </ul>
              
              <p className="text-red-600 font-bold"><strong>We never sell your personal information to third parties.</strong></p>

              <h2 className="text-red-600 font-black">5. Data Security</h2>
              
              <p className="text-gray-900">
                We implement industry-standard security measures to protect your information:
              </p>
              <ul>
                <li>Encryption of data in transit and at rest</li>
                <li>Regular security audits and penetration testing</li>
                <li>Access controls and employee training</li>
                <li>Secure backup and disaster recovery procedures</li>
                <li>Compliance with ISO 27001 security standards</li>
              </ul>

              <h2 className="text-red-600 font-black">6. Data Retention</h2>
              
              <p className="text-gray-900">We retain your information for as long as necessary to:</p>
              <ul>
                <li>Provide ongoing services to you</li>
                <li>Comply with legal obligations</li>
                <li>Resolve disputes and enforce agreements</li>
                <li>Maintain business records (typically 7 years for financial records)</li>
              </ul>
              
              <p className="text-gray-900">
                You can request deletion of your data at any time (subject to legal requirements).
              </p>

              <h2 className="text-red-600 font-black">7. Your Rights (GDPR)</h2>
              
              <p className="text-gray-900">If you're an EU resident, you have the right to:</p>
              <ul>
                <li><strong>Access:</strong> Request a copy of your personal data</li>
                <li><strong>Rectification:</strong> Correct inaccurate or incomplete information</li>
                <li><strong>Erasure:</strong> Request deletion of your data ("right to be forgotten")</li>
                <li><strong>Portability:</strong> Receive your data in a machine-readable format</li>
                <li><strong>Restriction:</strong> Limit how we process your data</li>
                <li><strong>Objection:</strong> Opt out of certain types of processing</li>
                <li><strong>Withdraw Consent:</strong> Remove consent for marketing communications</li>
              </ul>
              
              <p className="text-gray-900">
                To exercise these rights, contact us at <a href="mailto:privacy@audioandco.com" className="text-red-600 font-bold hover:underline">privacy@audioandco.com</a>
              </p>

              <h2 className="text-red-600 font-black">8. Cookies and Tracking</h2>
              
              <p className="text-gray-900">We use cookies and similar technologies to:</p>
              <ul>
                <li><strong>Essential Cookies:</strong> Enable core website functionality</li>
                <li><strong>Performance Cookies:</strong> Analyze website usage and improve performance</li>
                <li><strong>Marketing Cookies:</strong> Deliver relevant advertisements and content</li>
              </ul>
              
              <p className="text-gray-900">
                You can control cookie settings through your browser or our cookie preference center.
              </p>

              <h2 className="text-red-600 font-black">9. Third-Party Services</h2>
              
              <p className="text-gray-900">We work with trusted third-party services:</p>
              <ul>
                <li><strong>Google Analytics (GA4):</strong> Website performance and user behaviour analysis</li>
                <li><strong>Microsoft Clarity:</strong> Session replay and heatmap analysis to improve user experience</li>
                <li><strong>Stripe/PayPal:</strong> Secure payment processing</li>
                <li><strong>Brevo (formerly Sendinblue):</strong> Email delivery for audit reports and marketing (with your consent)</li>
                <li><strong>MongoDB:</strong> Secure database storage for audit results and lead information</li>
                <li><strong>Zoom:</strong> Video consultations and meetings</li>
              </ul>
              
              <p className="text-gray-900">
                Each service has its own privacy policy, which we encourage you to review.
              </p>

              <h2 className="text-red-600 font-black">10. International Data Transfers</h2>
              
              <p className="text-gray-900">
                We may transfer your data outside the UK/EU to provide our services. 
                When we do, we ensure adequate protection through:
              </p>
              <ul>
                <li>European Commission adequacy decisions</li>
                <li>Standard Contractual Clauses (SCCs)</li>
                <li>Binding Corporate Rules</li>
                <li>Certification schemes (Privacy Shield successors)</li>
              </ul>

              <h2 className="text-red-600 font-black">11. Children's Privacy</h2>
              
              <p className="text-gray-900">
                Our services are not intended for children under 16. We do not knowingly collect 
                personal information from children. If we discover we have collected information 
                from a child, we will delete it immediately.
              </p>

              <h2 className="text-red-600 font-black">12. Changes to This Policy</h2>
              
              <p className="text-gray-900">
                We may update this privacy policy to reflect changes in our practices or legal requirements. 
                We'll notify you of significant changes via email or website notice.
              </p>

              <h2 className="text-red-600 font-black">13. Contact Information</h2>
              
              <div className="bg-gray-50 p-6 rounded-lg">
                <h3 className="font-black text-red-600 mb-4">Data Protection Officer</h3>
                <div className="space-y-3">
                  <div className="flex items-center space-x-3">
                    <Mail className="w-5 h-5 text-red-600" />
                    <span>Email: <a href="mailto:privacy@audioandco.com" className="text-red-600 font-bold hover:underline">privacy@audioandco.com</a></span>
                  </div>
                  <div className="flex items-center space-x-3">
                    <Phone className="w-5 h-5 text-red-600" />
                    <span>Phone: +44 (0) 203 983 8484</span>
                  </div>
                  <div className="flex items-start space-x-3">
                    <div className="mt-1">
                      <div className="w-5 h-5 text-red-600">
                        📍
                      </div>
                    </div>
                    <div>
                      <strong>Audio & Co.®</strong><br />
                      Data Protection Officer<br />
                      London, United Kingdom
                    </div>
                  </div>
                </div>
              </div>

              <h2 className="text-red-600 font-black">14. Supervisory Authority</h2>
              
              <p className="text-gray-900">
                If you have concerns about how we handle your data, you can contact the UK 
                Information Commissioner's Office (ICO):
              </p>
              <ul>
                <li>Website: <a href="https://ico.org.uk" target="_blank" rel="noopener noreferrer" className="text-red-600 font-bold hover:underline">ico.org.uk</a></li>
                <li>Phone: 0303 123 1113</li>
                <li>Post: Information Commissioner's Office, Wycliffe House, Water Lane, Wilmslow, Cheshire SK9 5AF</li>
              </ul>
            </div>

            {/* Footer Links */}
            <div className="mt-12 pt-8 border-t border-gray-200">
              <div className="flex flex-col sm:flex-row justify-between items-center space-y-4 sm:space-y-0">
                <div className="flex space-x-6 text-sm">
                  <Link to="/terms" className="text-red-600 font-bold hover:underline">
                    Terms & Conditions
                  </Link>
                  <Link to="/contact" className="text-red-600 font-bold hover:underline">
                    Contact Us
                  </Link>
                  <Link to="/" className="text-red-600 font-bold hover:underline">
                    Homepage
                  </Link>
                </div>
                <div className="text-sm text-gray-500">
                  Last updated: April 2026
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Privacy;